import React, { useState, useRef, useEffect, ReactNode, ReactElement } from 'react';
import { createPortal } from 'react-dom';

interface DropdownMenuProps {
  children: ReactNode;
}

interface DropdownMenuTriggerProps {
  children: ReactElement;
  asChild?: boolean;
  onClick?: () => void;
}

interface DropdownMenuContentProps {
  children: ReactNode;
  align?: string;
  className?: string;
  onClose?: () => void;
  closing?: boolean;
  triggerRef?: React.RefObject<HTMLElement>;
  contentRef?: React.RefObject<HTMLDivElement>;
}

interface DropdownMenuItemProps {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
  onClose?: () => void;
}

export function DropdownMenu({ children }: DropdownMenuProps) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInsideContainer = containerRef.current?.contains(target);
      const isClickInsideContent = contentRef.current?.contains(target);

      if (!isClickInsideContainer && !isClickInsideContent && open) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Update trigger ref when open state changes
  useEffect(() => {
    if (open && containerRef.current) {
      const button = containerRef.current.querySelector('button');
      if (button) {
        (triggerRef as any).current = button;
      }
    }
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return null;
        const childType = child.type as any;
        if (childType?.displayName === 'DropdownMenuTrigger') {
          return React.cloneElement(child as ReactElement<any>, {
            onClick: () => {
              if (open) {
                handleClose();
              } else {
                setOpen(true);
              }
            },
          });
        }
        if (childType?.displayName === 'DropdownMenuContent') {
          return open
            ? React.cloneElement(child as ReactElement<any>, {
                onClose: handleClose,
                closing,
                triggerRef,
                contentRef,
              })
            : null;
        }
        return child;
      })}
    </div>
  );
}

export function DropdownMenuTrigger({ children, asChild, onClick }: DropdownMenuTriggerProps) {
  if (asChild) {
    return React.cloneElement(children as React.ReactElement, { onClick } as any);
  }
  return <button onClick={onClick}>{children}</button>;
}
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

export function DropdownMenuContent({
  children,
  align = 'end',
  className = '',
  onClose,
  closing,
  triggerRef,
  contentRef: externalContentRef,
}: DropdownMenuContentProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const internalContentRef = useRef<HTMLDivElement>(null);
  const contentRef = externalContentRef || internalContentRef;

  useEffect(() => {
    if (!triggerRef?.current || !contentRef.current) return;

    const updatePosition = () => {
      if (!triggerRef?.current || !contentRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const contentRect = contentRef.current.getBoundingClientRect();

      // Using fixed positioning, so coordinates are relative to viewport
      let top = triggerRect.bottom + 8; // mt-2 = 8px
      let left = triggerRect.left;

      if (align === 'end') {
        left = triggerRect.right - contentRect.width;
      }

      // Adjust if dropdown goes off screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (left + contentRect.width > viewportWidth) {
        left = viewportWidth - contentRect.width - 8;
      }
      if (left < 0) {
        left = 8;
      }

      if (top + contentRect.height > viewportHeight) {
        // Show above trigger if not enough space below
        top = triggerRect.top - contentRect.height - 8;
      }
      if (top < 0) {
        top = 8;
      }

      setPosition({ top, left });
    };

    // Small delay to ensure content is rendered
    const timeoutId = setTimeout(updatePosition, 0);

    // Update position on scroll and resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [align, triggerRef, contentRef]);

  const content = (
    <div
      ref={contentRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      className={`bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999] min-w-[200px] origin-top ${closing ? 'animate-dropdown-close' : 'animate-dropdown'} ${className}`}
    >
      {React.Children.map(children, child => {
        if (!React.isValidElement(child)) return null;
        const childType = child.type as any;
        if (childType?.displayName === 'DropdownMenuItem') {
          return React.cloneElement(child as ReactElement<any>, { onClose });
        }
        return child;
      })}
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(content, document.body) : null;
}
DropdownMenuContent.displayName = 'DropdownMenuContent';

export function DropdownMenuItem({
  children,
  onClick,
  className = '',
  onClose,
}: DropdownMenuItemProps) {
  const handleClick = () => {
    onClick?.();
    onClose?.();
  };

  return (
    <div
      onClick={handleClick}
      className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center text-sm text-gray-700 ${className}`}
    >
      {children}
    </div>
  );
}
DropdownMenuItem.displayName = 'DropdownMenuItem';

export function DropdownMenuSeparator() {
  return <div className="border-t border-gray-200 my-1" />;
}
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';
